import { adminFetch } from "./client";
import { getConfig } from "@/config";

export interface Branding {
  group_id: string | null;
  group_slug: string;
  group_name: string;
  company_name: string;
  page_title: string;
  copyright_text: string | null;
  primary_color: string;
  accent_color: string;
  support_email: string | null;
  support_phone: string | null;
  address: string | null;
  website: string | null;
  whatsapp_phone: string | null;
  portal_url: string | null;
  admin_url: string | null;
  from_email: string | null;
  owner_email: string | null;
  /** Platform -> profile URL, e.g. {\"instagram\": \"https://...\"}. */
  social_links: Record<string, string>;
  /** Contracting entity for the legal pages — distinct from the trading
   *  brand in `company_name` (MPPI-00301). */
  legal_entity?: string | null;
  /** e.g. "RERA ORN: 55801 · DED License: 1556699". Omitted when unset —
   *  never defaulted, because it is a claim about who may trade. */
  registration_text?: string | null;
  tagline?: string | null;
  privacy_email?: string | null;
  contact_notification_email?: string | null;
  logo_url: string | null;
  favicon_url: string | null;
}

/** Default brand — the PRE-RESOLUTION placeholder, and the hard fallback if
 *  `/branding/by-domain` fails.
 *
 *  It carries the default palette and nothing else. Every identity-bearing
 *  field — company name, contact details, address, social profiles, portal
 *  URLs — is deliberately blank, because this object is rendered on EVERY
 *  domain during the cold load. Seeding it with Mina Prime's details meant a
 *  HouseKey visitor briefly saw Mina Prime's Facebook, phone number and
 *  Business Bay address, and saw them permanently if the branding call errored
 *  (MPPI-00301).
 *
 *  The same reasoning as `NON_INHERITABLE` on the API side: a tenant that has
 *  not configured a contact detail must show NO contact detail, never another
 *  tenant's. Components already omit these when unset, so blank degrades to a
 *  quieter footer rather than a broken one.
 *
 *  The colours stay, because the alternative is a flash of unstyled chrome and
 *  a palette is not an identity claim. */
export const DEFAULT_BRANDING: Branding = {
  group_id: null,
  group_slug: "",
  group_name: "",
  company_name: "",
  page_title: "Property Intelligence",
  copyright_text: null,
  primary_color: "#012943",
  accent_color: "#CEAC53",
  support_email: null,
  support_phone: null,
  address: null,
  website: null,
  whatsapp_phone: null,
  portal_url: null,
  admin_url: null,
  from_email: null,
  owner_email: null,
  social_links: {},
  legal_entity: null,
  registration_text: null,
  tagline: null,
  privacy_email: null,
  contact_notification_email: null,
  logo_url: null,
  favicon_url: null,
};

export function brandingAssetUrl(rel: string | null): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${getConfig().apiBaseUrl}${rel}`;
}

export async function fetchMyBranding(): Promise<Branding> {
  return adminFetch<Branding>("/api/v1/branding/me");
}

/** Pre-auth: resolve the login-page brand from the current hostname. */
export async function fetchBrandingByDomain(): Promise<Branding> {
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  return adminFetch<Branding>(
    `/api/v1/branding/by-domain?host=${encodeURIComponent(host)}`,
  );
}
